import { useCallback, useEffect, useState } from 'react';
import { PaperclipIcon } from 'lucide-react';
import {
  DECLARATIE_SOORT_LABEL,
  type DeclaratieRegel,
  type DeclaratieSoort,
  type DeclaratieStatus,
} from '@kennisbank/shared';
import { apiGet, apiPost, ApiError } from '../../api/client.js';
import { safeHttpsHref } from '../../lib/url.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { DocumentPreview, type PreviewDoc } from '../DocumentPreview.js';
import { euro, formatDatum, STATUS_CHIP, STATUS_LABEL } from './labels.js';

/**
 * Wachtrij voor beoordelaars. Op twee plekken in gebruik: de pagina Beheer
 * Declaraties (kantoor/admin) en de sectie "Te beoordelen" op de
 * declaratiepagina van een teamleider. De backend bepaalt het bereik (eigen
 * team of alles); dit component toont wat er terugkomt.
 *
 * Elke declaratie staat volledig open op een kaart met de knoppen erop
 * (prototype-keuze Jos 12 okt 2026, kaart uit C onder elkaar zoals A): de
 * eerdere opzet met uitklappen en een aparte verstuurknop na akkoord werd in
 * de praktijk niet gevonden. Akkoord mailt meteen naar de administratie.
 */

interface LijstRij {
  id: string;
  declaratienummer: string;
  naam: string;
  periode: string;
  soort?: DeclaratieSoort;
  directNaarAdministratie?: boolean;
  totaalCent: number;
  vergoedingPercentage?: number;
  vergoedCent?: number;
  afwijzingsreden?: string;
  regels: DeclaratieRegel[];
  bonnen: Array<{ filename: string; sizeBytes: number }>;
  rekeningnummer: string;
  woonplaats?: string;
  status: DeclaratieStatus;
  submittedByUserId: string;
  createdAt: string;
}

const FILTERS: Array<{ key: DeclaratieStatus | 'alle'; label: string }> = [
  { key: 'ingediend', label: 'Te beoordelen' },
  { key: 'akkoord', label: 'Akkoord, niet verstuurd' },
  { key: 'verstuurd', label: 'Verstuurd' },
  { key: 'afgewezen', label: 'Afgewezen' },
  { key: 'alle', label: 'Alles' },
];

export function BeoordeelLijst(): JSX.Element {
  const { user } = useAuth();
  const [rows, setRows] = useState<LijstRij[]>([]);
  const [eigenToegestaan, setEigenToegestaan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [filter, setFilter] = useState<DeclaratieStatus | 'alle'>('ingediend');

  const load = useCallback((): void => {
    const params = filter === 'alle' ? undefined : { status: filter };
    void apiGet<{ declaraties: LijstRij[]; eigenToegestaan?: boolean }>(
      '/api/v1/beheer/declaraties',
      params,
    )
      .then((d) => {
        setRows(d.declaraties);
        setEigenToegestaan(d.eigenToegestaan === true);
        setErrorMsg(null);
      })
      .catch((err) => {
        setErrorMsg(
          err instanceof ApiError && err.status === 403
            ? 'Geen toegang tot de declaratiewachtrij.'
            : 'De wachtrij kon niet geladen worden. Probeer het opnieuw.',
        );
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => {
              setFilter(f.key);
              setMelding(null);
            }}
            className={`rounded-full px-3 py-1 text-[13px] font-medium ${
              filter === f.key
                ? 'bg-brand-600 text-white'
                : 'border border-brand-100 bg-white text-ink-900/70 hover:bg-brand-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {melding && (
        <p role="status" className="rounded-lg bg-brand-50 px-4 py-3 text-[14px] text-ink-900/80">
          {melding}
        </p>
      )}
      {errorMsg && (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">{errorMsg}</p>
      )}
      {loading && <p className="text-[14px] text-ink-900/60">Laden…</p>}
      {!loading && !errorMsg && rows.length === 0 && (
        <p className="rounded-2xl border border-brand-100 bg-white p-5 text-[14px] text-ink-900/60">
          Geen declaraties in deze weergave.
        </p>
      )}

      <ul className="space-y-4">
        {rows.map((rij) => (
          <li key={rij.id}>
            <DeclaratieKaart
              rij={rij}
              eigen={rij.submittedByUserId === user?.id && !eigenToegestaan}
              onKlaar={(tekst) => {
                setMelding(tekst);
                load();
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeclaratieKaart({
  rij,
  eigen,
  onKlaar,
}: {
  rij: LijstRij;
  eigen: boolean;
  onKlaar: (melding: string) => void;
}): JSX.Element {
  const [bezig, setBezig] = useState(false);
  const [foutMsg, setFoutMsg] = useState<string | null>(null);
  const [paneel, setPaneel] = useState<'deels' | 'afwijzen' | null>(null);
  const [percentage, setPercentage] = useState('');
  const [reden, setReden] = useState('');
  const [bonOpen, setBonOpen] = useState<PreviewDoc | null>(null);

  const pct = Number(percentage);
  const pctGeldig = Number.isInteger(pct) && pct >= 1 && pct <= 99;

  // Unieke sleutel per bon: twee bonnen kunnen dezelfde bestandsnaam hebben.
  // De extensie blijft achteraan, daar kiest de viewer het type op.
  const bonDocs: PreviewDoc[] = rij.bonnen.map((b, i) => ({
    filename: `${i + 1}-${b.filename}`,
    documentTitle: b.filename,
    sizeBytes: b.sizeBytes,
  }));
  // Signed URL's alleen op aanvraag uit de detailroute, nooit in de lijst.
  const bonUrl = async (filename: string): Promise<string> => {
    const i = bonDocs.findIndex((d) => d.filename === filename);
    const d = await apiGet<{ bonnen: Array<{ url?: string }> }>(
      `/api/v1/beheer/declaraties/${rij.id}`,
    );
    const url = safeHttpsHref(d.bonnen[i]?.url);
    if (!url) throw new Error('bon niet beschikbaar');
    return url;
  };

  async function actie(pad: string, body: unknown): Promise<void> {
    setBezig(true);
    setFoutMsg(null);
    try {
      const res = await apiPost<{ status: DeclaratieStatus; verzendFout?: string }>(
        `/api/v1/beheer/declaraties/${rij.id}/${pad}`,
        body,
      );
      onKlaar(
        res.verzendFout
          ? `${rij.declaratienummer} is akkoord, maar versturen mislukte: ${res.verzendFout} Je vindt hem onder "Akkoord, niet verstuurd".`
          : res.status === 'afgewezen'
            ? `${rij.declaratienummer} is afgewezen. ${rij.naam} krijgt een melding.`
            : `${rij.declaratienummer} is akkoord en naar de administratie verstuurd.`,
      );
    } catch (err) {
      setFoutMsg(
        err instanceof ApiError ? err.message : 'De actie is mislukt. Probeer het opnieuw.',
      );
    } finally {
      setBezig(false);
    }
  }

  const knopPrimair =
    'rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-bold text-white hover:bg-brand-700 disabled:opacity-50';
  const knopSecundair =
    'rounded-xl border border-brand-200 px-4 py-2.5 text-[14px] font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50';

  return (
    <div className="rounded-2xl border border-brand-100 bg-white p-4 sm:p-5">
      {bonOpen && (
        <DocumentPreview
          doc={bonOpen}
          docs={bonDocs}
          onSelect={setBonOpen}
          onClose={() => setBonOpen(null)}
          getUrl={bonUrl}
          meta={
            <p className="text-sm text-ink-900/70">
              Bon bij declaratie {rij.declaratienummer} van {rij.naam}.
            </p>
          }
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[18px] font-bold text-ink-900">{rij.naam}</p>
          <p className="text-[13px] text-ink-900/60">
            {rij.declaratienummer} · {rij.periode}
            {rij.soort && ` · ${DECLARATIE_SOORT_LABEL[rij.soort]}`}
            {rij.directNaarAdministratie && ' · Kantoor, zonder akkoord'} · ingediend{' '}
            {formatDatum(rij.createdAt)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[22px] font-bold tabular-nums text-ink-900">
            {euro(rij.vergoedCent ?? rij.totaalCent)}
          </p>
          {rij.vergoedingPercentage && (
            <p className="text-[12px] text-ink-900/60">
              {rij.vergoedingPercentage}% van {euro(rij.totaalCent)}
            </p>
          )}
          <span
            className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[12px] font-medium ${STATUS_CHIP[rij.status]}`}
          >
            {STATUS_LABEL[rij.status]}
          </span>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[14px]">
          <tbody>
            {rij.regels.map((r, i) => (
              <tr key={i} className="border-t border-brand-100/60">
                <td className="whitespace-nowrap py-1.5 pr-4">{formatDatum(r.datum)}</td>
                <td className="py-1.5 pr-4">{r.omschrijving}</td>
                <td className="py-1.5 text-right tabular-nums">{euro(r.bedragCent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {bonDocs.map((bon) => (
          <button
            key={bon.filename}
            type="button"
            onClick={() => setBonOpen(bon)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-1.5 text-[13px] text-brand-700 hover:border-brand-400"
          >
            <PaperclipIcon size={14} aria-hidden="true" />
            {bon.documentTitle}
          </button>
        ))}
        {bonDocs.length === 0 && <span className="text-[13px] text-ink-900/50">Geen bonnen</span>}
        <span className="ml-auto text-[13px] text-ink-900/60">
          {rij.woonplaats && <>{rij.woonplaats} · </>}IBAN{' '}
          <span className="font-mono">{rij.rekeningnummer}</span>
        </span>
      </div>

      {rij.status === 'afgewezen' && rij.afwijzingsreden && (
        <p className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">
          Afgewezen: {rij.afwijzingsreden}
        </p>
      )}

      {foutMsg && (
        <p className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">{foutMsg}</p>
      )}

      {eigen && (rij.status === 'ingediend' || rij.status === 'akkoord') && (
        <p className="mt-4 border-t border-brand-100/60 pt-3 text-[13px] text-ink-900/60">
          Dit is je eigen declaratie; een collega moet deze beoordelen.
        </p>
      )}

      {!eigen && rij.status === 'ingediend' && (
        <div className="mt-4 space-y-3 border-t border-brand-100/60 pt-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={bezig}
              onClick={() => void actie('review', { action: 'akkoord' })}
              className={knopPrimair}
            >
              {bezig ? 'Versturen…' : 'Akkoord en versturen'}
            </button>
            <button
              type="button"
              disabled={bezig}
              onClick={() => setPaneel(paneel === 'deels' ? null : 'deels')}
              className={knopSecundair}
            >
              Deels akkoord…
            </button>
            <button
              type="button"
              disabled={bezig}
              onClick={() => setPaneel(paneel === 'afwijzen' ? null : 'afwijzen')}
              className="rounded-xl border border-red-300 px-4 py-2.5 text-[14px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Afwijzen…
            </button>
          </div>

          {paneel === 'deels' && (
            <div className="space-y-2 rounded-xl bg-brand-50/60 p-4">
              <label className="block text-[14px] text-ink-900/80">
                Welk percentage vergoedt SUPPOHRT? Bijvoorbeeld bij een lidmaatschap.
                <span className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={99}
                    value={percentage}
                    onChange={(e) => setPercentage(e.target.value)}
                    className="w-20 rounded-lg border border-brand-100 bg-white px-3 py-2 text-[14px] tabular-nums"
                  />
                  <span>%</span>
                  {pctGeldig && (
                    <span className="text-[14px] font-semibold text-ink-900">
                      = {euro(Math.round((rij.totaalCent * pct) / 100))} van {euro(rij.totaalCent)}
                    </span>
                  )}
                </span>
              </label>
              <button
                type="button"
                disabled={bezig || !pctGeldig}
                onClick={() =>
                  void actie('review', { action: 'akkoord', vergoedingPercentage: pct })
                }
                className={knopPrimair}
              >
                {bezig ? 'Versturen…' : `Akkoord voor ${pctGeldig ? pct : '…'}% en versturen`}
              </button>
            </div>
          )}

          {paneel === 'afwijzen' && (
            <div className="space-y-2">
              <textarea
                value={reden}
                onChange={(e) => setReden(e.target.value)}
                rows={2}
                placeholder="Reden voor afwijzing (verplicht)"
                className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
              <button
                type="button"
                disabled={bezig || reden.trim().length === 0}
                onClick={() => void actie('review', { action: 'afwijzen', reden: reden.trim() })}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Afwijzen bevestigen
              </button>
            </div>
          )}
        </div>
      )}

      {!eigen && rij.status === 'akkoord' && (
        <div className="mt-4 space-y-2 border-t border-brand-100/60 pt-4">
          <p className="text-[14px] text-ink-900/80">
            Akkoord gegeven, maar de mail naar de administratie is nog niet gelukt.
          </p>
          <button
            type="button"
            disabled={bezig}
            onClick={() => void actie('verstuurd', {})}
            className={knopPrimair}
          >
            {bezig ? 'Versturen…' : 'Opnieuw versturen'}
          </button>
        </div>
      )}
    </div>
  );
}
